<template>
  <div
    class="relative flex w-full font-regular p-4 text-gray-900 bg-white rounded-lg shadow-xl shadow-blue-gray-900/5 bg-clip-border dark:bg-gray-800 dark:text-gray-200"
  >
    <div class="w-full">
      <!-- Header Section -->
      <div class="w-full flex flex-wrap gap-2 justify-between">
        <div>
          <h3
            class="block dark:text-gray-200 text-xl antialiased font-medium leading-snug tracking-normal"
          >
            Business Overview
          </h3>
          <p
            class="text-sm mt-2 font-normal leading-6 text-gray-800 opacity-80 dark:text-gray-300"
          >
            Here is how your business is doing today
          </p>
        </div>
        <div></div>
      </div>

      <!-- Metrics Grid -->
      <div class="flex flex-wrap mt-6 w-full mb-5">
        <div class="w-full sm:w-1/2 md:w-1/4 p-1">
          <div
            class="w-full p-3 bg-red-800/10 hover:bg-red-800/20 rounded-lg cursor-pointer h-full flex justify-between items-center dark:bg-red-800/20 dark:hover:bg-red-800/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">
                {{ dailySales.totalOrders }}
              </h3>
              <p
                class="text-xs mt-1 font-normal text-gray-800 dark:text-gray-300"
              >
                Orders Today
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-red-800 dark:text-red-400"
            >
              <i class="fa-solid fa-receipt text-[20px]"></i>
            </div>
          </div>
        </div>
        <div class="w-full sm:w-1/2 md:w-1/4 p-1">
          <div
            class="flex justify-between items-center w-full h-full p-3 bg-blue-800/10 hover:bg-blue-800/20 rounded-lg cursor-pointer dark:bg-blue-800/20 dark:hover:bg-blue-800/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">
                {{ dailySales.productSold }}
              </h3>
              <p
                class="text-xs mt-1 font-normal text-gray-800 dark:text-gray-300"
              >
                Products Sold
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-blue-800 dark:text-blue-400"
            >
              <i class="fa-solid fa-tags text-[20px]"></i>
            </div>
          </div>
        </div>
        <div class="w-full sm:w-1/2 md:w-1/4 p-1">
          <div
            class="flex justify-between items-center w-full h-full bg-yellow-800/10 hover:bg-yellow-800/20 rounded-lg cursor-pointer p-3 dark:bg-yellow-800/20 dark:hover:bg-yellow-800/30"
          >
            <div>
              <h3 class="font-medium text-xl dark:text-gray-200">0</h3>
              <p
                class="text-xs mt-1 font-normal text-gray-800 dark:text-gray-300"
              >
                Website Visits
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-yellow-800 dark:text-yellow-400"
            >
              <i class="fa-solid fa-computer-mouse text-[20px]"></i>
            </div>
          </div>
        </div>
        <div class="w-full sm:w-1/2 md:w-1/4 p-1">
          <div
            class="flex justify-between items-center w-full h-full bg-main-50 hover:bg-main-100 rounded-lg cursor-pointer p-3 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            <div>
              <h3 class="font-medium text-xl text-main-800 dark:text-gray-200">
                {{
                  Number(dailySales.totalEarnings).toLocaleString("en-NG", {
                    style: "currency",
                    currency: "NGN",
                  })
                }}
              </h3>
              <p
                class="text-xs mt-1 font-normal text-main-800 dark:text-gray-300"
              >
                Total Earnings
              </p>
            </div>
            <div
              class="w-[32px] h-[32px] flex justify-center items-center p-[4px] text-main-800 dark:text-gray-200"
            >
              <i class="fa-solid fa-naira-sign text-[20px]"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Divider -->
      <hr
        class="border-[1px] border-gray-600/10 px-10 mt-[15px] dark:border-gray-700"
      />

      <!-- Sales Section -->
      <div class="mt-[15px] w-full">
        <div class="flex justify-between">
          <div class="flex flex-wrap w-full mb-5">
            <div class="w-full md:w-1/2 xl:w-1/2 p-2">
              <div
                class="w-full flex-col p-3 rounded-lg cursor-pointer flex justify-start items-start shadow shadow-main-200 dark:shadow-gray-700"
              >
                <div
                  class="icon bg-main-50 rounded-lg w-[32px] h-[32px] flex justify-center items-center p-[4px] text-main-800 dark:bg-gray-700 dark:text-gray-200"
                >
                  <i class="fa-solid fa-cart-shopping text-[16px]"></i>
                </div>
                <div>
                  <p
                    class="text-xs mt-[12px] mb-[4px] font-normal text-main-800 dark:text-gray-300"
                  >
                    Total Sales Online
                  </p>
                  <h3
                    class="font-medium text-xl text-main-800 dark:text-gray-200"
                  >
                    {{
                      Number(dailySales.totalSalesOnline).toLocaleString(
                        "en-NG",
                        {
                          style: "currency",
                          currency: "NGN",
                        }
                      )
                    }}
                  </h3>
                </div>
              </div>
            </div>
            <div class="w-full md:w-1/2 xl:w-1/2 p-2">
              <div
                class="w-full flex-col p-3 rounded-lg cursor-pointer flex justify-start items-start shadow shadow-main-200 dark:shadow-gray-700"
              >
                <div
                  class="icon bg-main-50 rounded-lg w-[32px] h-[32px] flex justify-center items-center p-[4px] text-main-800 dark:bg-gray-700 dark:text-gray-200"
                >
                  <i class="fa-solid fa-cash-register text-[16px]"></i>
                </div>
                <div>
                  <p
                    class="text-xs mt-[12px] mb-[4px] font-normal text-main-800 dark:text-gray-300"
                  >
                    Total Sales Offline
                  </p>
                  <h3
                    class="font-medium text-xl text-main-800 dark:text-gray-200"
                  >
                    {{
                      Number(dailySales.totalSalesOffline).toLocaleString(
                        "en-NG",
                        {
                          style: "currency",
                          currency: "NGN",
                        }
                      )
                    }}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart Section -->
      <SalesComparisionChart v-if="!loading" :monthlySales="monthlySales" />
      <template v-else>
        <div
          class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
        >
          <LoadingComponent /></div
      ></template>
    </div>
  </div>
</template>

<script>
import SalesComparisionChart from "./salesComparison.vue";
import { mapActions } from "vuex";
export default {
  name: "DailyBusinessOverview",
  data() {
    return {
      loading: true,
      dailySales: {
        totalOrders: 0,
        productSold: 0,
        totalEarnings: "0.00",
        totalSalesOnline: "0.00",
        totalSalesOffline: "0.00",
      },
      monthlySales: {
        1: { online: 0, offline: 0 },
        2: { online: 0, offline: 0 },
        3: { online: 0, offline: 0 },
        4: { online: 0, offline: 0 },
        5: { online: 0, offline: 0 },
        6: { online: 0, offline: 0 },
        7: { online: 0, offline: 0 },
        8: { online: 0, offline: 0 },
        9: { online: 0, offline: 0 },
        10: { online: 0, offline: 0 },
        11: { online: 0, offline: 0 },
        12: { online: 0, offline: 0 },
      },
    };
  },
  components: {
    SalesComparisionChart,
  },
  methods: {
    ...mapActions(["fetchDailyBusinessOverview"]),
    async fetchData() {
      this.loading = true;
      try {
        const response = await this.fetchDailyBusinessOverview();
        this.dailySales = { ...this.dailySales, ...response?.data?.dailySales };
        this.monthlySales = {
          ...this.monthlySales,
          ...response?.data?.monthlySales,
        };
      } catch (error) {
        console.error("Error fetching daily business overview:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
